import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Search, Trash2, Pencil } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import QRDialog from "@/components/QRDialog";
import ConfirmTypeDialog from "@/components/ConfirmTypeDialog";

const CATEGORIES = [
  { v: "industrial_equipment", l: "Industrial Equipment" },
  { v: "vehicle", l: "Vehicle / Fleet" },
  { v: "generic", l: "Generic" },
];
const STATUSES = [
  { v: "active", l: "Active" },
  { v: "out_of_service", l: "Out of Service" },
  { v: "retired", l: "Retired" },
];

const emptyForm = { name: "", asset_tag: "", category: "industrial_equipment", location: "", status: "active", manufacturer: "", model: "", serial_number: "", purchase_date: "", notes: "", customer_email: "", assigned_inspector_id: "" };

export default function Equipment() {
  const { user } = useAuth();
  const canEdit = ["admin","super_admin"].includes(user?.role) || user?.role === "inspector";
  const [equipment, setEquipment] = useState([]);
  const [inspectors, setInspectors] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [filters, setFilters] = useState({ category: "all", status: "all", q: "" });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);   // null = create mode; object = edit mode
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = async () => {
    const params = {};
    if (filters.category !== "all") params.category = filters.category;
    if (filters.status !== "all") params.status = filters.status;
    if (filters.q) params.q = filters.q;
    const { data } = await api.get("/assets", { params });
    setEquipment(data);
  };

  useEffect(() => {
    load();
    if (["admin","super_admin"].includes(user?.role) || user?.role === "inspector") {
      api.get("/users").then(({ data }) => setInspectors(data.filter(u => ["inspector", "admin"].includes(u.role)))).catch(() => {});
      api.get("/customers").then(({ data }) => setCustomers(data)).catch(() => {});
    }
  }, [filters.category, filters.status, user]);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (a) => {
    setEditing(a);
    setForm({
      name: a.name || "",
      asset_tag: a.asset_tag || "",
      category: a.category || "industrial_equipment",
      location: a.location || "",
      status: a.status || "active",
      manufacturer: a.manufacturer || "",
      model: a.model || "",
      serial_number: a.serial_number || "",
      purchase_date: a.purchase_date || "",
      notes: a.notes || "",
      customer_email: a.customer_email || "",
      assigned_inspector_id: a.assigned_inspector_id || "",
    });
    setOpen(true);
  };

  const submit = async () => {
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/assets/${editing.id}`, form);
        toast.success("Equipment updated");
      } else {
        await api.post("/assets", form);
        toast.success("Equipment created");
      }
      setOpen(false); setEditing(null); setForm(emptyForm); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const removeEquipment = async (id) => {
    try {
      await api.delete(`/assets/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-6 md:p-8" data-testid="equipment-page">
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-1">Registry</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Equipment</h1>
        </div>
        {canEdit && (
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditing(null); }}>
            <DialogTrigger asChild>
              <Button data-testid="new-equipment-button" onClick={openCreate} className="rounded-full bg-[#01ADEF] hover:bg-[#0198D6]">
                <Plus className="w-4 h-4 mr-2" /> New Equipment
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl rounded-md" data-testid="equipment-dialog">
              <DialogHeader><DialogTitle className="font-display">{editing ? `Edit Equipment — ${editing.name}` : "Register New Equipment"}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-4 py-2">
                <Field label="Equipment Name *"><Input data-testid="equipment-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-md" /></Field>
                <Field label="Equipment Tag *"><Input data-testid="equipment-tag-input" value={form.asset_tag} onChange={(e) => setForm({ ...form, asset_tag: e.target.value })} className="rounded-md font-mono" /></Field>
                <Field label="Category"><Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}><SelectTrigger className="rounded-md"><SelectValue /></SelectTrigger><SelectContent>{CATEGORIES.map(c => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent></Select></Field>
                <Field label="Status"><Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}><SelectTrigger className="rounded-md"><SelectValue /></SelectTrigger><SelectContent>{STATUSES.map(s => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent></Select></Field>
                <Field label="Location *"><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="rounded-md" /></Field>
                <Field label="Manufacturer"><Input value={form.manufacturer} onChange={(e) => setForm({ ...form, manufacturer: e.target.value })} className="rounded-md" /></Field>
                <Field label="Model"><Input value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} className="rounded-md" /></Field>
                <Field label="Serial Number"><Input value={form.serial_number} onChange={(e) => setForm({ ...form, serial_number: e.target.value })} className="rounded-md font-mono" /></Field>
                <div className="col-span-2"><Field label="Customer (portal access)">
                  <Select value={form.customer_email || "none"} onValueChange={v => setForm({ ...form, customer_email: v === "none" ? "" : v })}>
                    <SelectTrigger className="rounded-md" data-testid="equipment-customer-select"><SelectValue placeholder="— No customer —" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— No customer —</SelectItem>
                      {customers.map(c => (
                        <SelectItem key={c.id} value={c.email} data-testid={`equipment-customer-option-${c.id}`}>
                          <span className="font-semibold">{c.name || c.email}</span>
                          {c.name && <span className="text-gray-400 ml-1 text-xs">— {c.email}</span>}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field></div>
                <div className="col-span-2"><Field label="Assigned Inspector (override customer default)">
                  <Select value={form.assigned_inspector_id || "none"} onValueChange={v => setForm({ ...form, assigned_inspector_id: v === "none" ? "" : v })}>
                    <SelectTrigger className="rounded-md" data-testid="equipment-inspector"><SelectValue placeholder="— Inherit from customer —" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— Inherit from customer —</SelectItem>
                      {inspectors.map(i => <SelectItem key={i.id} value={i.id}>{i.name} ({i.role})</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field></div>
                <div className="col-span-2"><Field label="Notes"><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-md" /></Field></div>
              </div>
              <DialogFooter>
                <Button variant="outline" className="rounded-full" onClick={() => { setOpen(false); setEditing(null); }}>Cancel</Button>
                <Button data-testid="save-equipment-button" disabled={saving || !form.name || !form.asset_tag || !form.location} onClick={submit} className="rounded-full bg-[#01ADEF] hover:bg-[#0198D6]">{saving ? "Saving..." : (editing ? "Save Changes" : "Create")}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-200 p-4 flex flex-wrap items-end gap-4 mb-4">
        <div className="flex-1 min-w-[220px]">
          <Label className="text-xs uppercase tracking-wider font-semibold text-gray-700">Search</Label>
          <div className="relative mt-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input data-testid="equipment-search-input" placeholder="Search name, tag or serial…" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} onKeyDown={(e) => e.key === "Enter" && load()} className="pl-9 rounded-md" />
          </div>
        </div>
        <div className="w-48">
          <Label className="text-xs uppercase tracking-wider font-semibold text-gray-700">Category</Label>
          <Select value={filters.category} onValueChange={(v) => setFilters({ ...filters, category: v })}>
            <SelectTrigger className="mt-1 rounded-md" data-testid="filter-category"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All</SelectItem>{CATEGORIES.map(c => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="w-40">
          <Label className="text-xs uppercase tracking-wider font-semibold text-gray-700">Status</Label>
          <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
            <SelectTrigger className="mt-1 rounded-md" data-testid="filter-status"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All</SelectItem>{STATUSES.map(s => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 overflow-x-auto">        <table className="w-full text-sm" data-testid="equipment-table">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50">
              <Th>Equipment</Th><Th>Tag</Th><Th>Customer</Th><Th>Category</Th><Th>Location</Th><Th>Inspector</Th><Th>Status</Th><Th></Th>
            </tr>
          </thead>
          <tbody>
            {equipment.length === 0 ? (
              <tr><td colSpan={8} className="text-center py-10 text-gray-500">No equipment yet. Register your first one.</td></tr>
            ) : equipment.map((a) => {
              const cust = customers.find(c => (c.email || "").toLowerCase() === (a.customer_email || "").toLowerCase());
              return (
                <tr key={a.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`equipment-row-${a.id}`}>
                  <Td><div className="font-semibold">{a.name}</div>{a.manufacturer && <div className="text-xs text-gray-500">{a.manufacturer} {a.model}</div>}</Td>
                  <Td className="font-mono">{a.asset_tag}</Td>
                  <Td data-testid={`equipment-customer-${a.id}`}>
                    {a.customer_email ? (
                      <>
                        <div className="text-sm font-semibold">{cust?.name || a.customer_email}</div>
                        {cust?.name && <div className="text-[10px] text-gray-500 font-mono">{a.customer_email}</div>}
                      </>
                    ) : <span className="text-gray-400 text-xs">Unassigned</span>}
                  </Td>
                  <Td className="text-gray-600">{a.category.replace('_',' ')}</Td>
                  <Td>{a.location}</Td>
                  <Td>
                    {a.inspector_name ? (
                      <div>
                        <div className="text-sm font-semibold">{a.inspector_name}</div>
                        {a.inherited_inspector && <div className="text-[10px] uppercase tracking-wider text-gray-500">inherited</div>}
                      </div>
                    ) : <span className="text-gray-400 text-xs">Unassigned</span>}
                  </Td>
                  <Td><StatusBadge status={a.status} /></Td>
                  <Td className="flex items-center gap-3">
                    <QRDialog assetId={a.id} assetTag={a.asset_tag} />
                    {canEdit && (
                      <button data-testid={`edit-equipment-${a.id}`} onClick={() => openEdit(a)} className="text-gray-400 hover:text-[#01ADEF]" title="Edit"><Pencil className="w-4 h-4" /></button>
                    )}
                    {user?.role === 'admin' && (
                      <button data-testid={`delete-equipment-${a.id}`} onClick={() => setConfirmDel(a)} className="text-gray-400 hover:text-[#DC2626]" title="Delete"><Trash2 className="w-4 h-4" /></button>
                    )}
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <ConfirmTypeDialog
        open={!!confirmDel}
        onOpenChange={(o) => !o && setConfirmDel(null)}
        phrase="DELETE"
        title={`Delete equipment "${confirmDel?.name || ''}"?`}
        description="This permanently removes the equipment. Related inspections and certificates will still reference the original equipment ID but the equipment record will be gone."
        confirmLabel="Delete Equipment"
        onConfirm={async () => { await removeEquipment(confirmDel.id); }}
        testidPrefix="equipment-delete"
      />
    </div>
  );
}

const Field = ({ label, children }) => (<div><Label className="text-xs uppercase tracking-wider font-semibold text-gray-700">{label}</Label><div className="mt-1">{children}</div></div>);
const Th = ({ children }) => <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-semibold text-gray-600">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 ${className}`}>{children}</td>;
const StatusBadge = ({ status }) => {
  const map = { active: ["bg-[#D1FAE5]", "text-[#065F46]", "border-[#6EE7B7]", "Active"], out_of_service: ["bg-[#FEF3C7]", "text-[#92400E]", "border-[#FCD34D]", "Out of Service"], retired: ["bg-gray-100", "text-gray-700", "border-gray-300", "Retired"] };
  const [bg, tx, br, label] = map[status] || map.active;
  return <span className={`inline-block text-xs font-bold px-2 py-1 uppercase tracking-wider border ${bg} ${tx} ${br}`}>{label}</span>;
};
